"use client";

export interface MenuNode {
  id: string; // Unique slug or identifier
  label: string;
  href: string;
  type: number;
  children?: MenuNode[];
  // Optional future-proof metadata
  icon?: string;
  badge?: string;
  image?: string;
  description?: string;
  featured?: boolean;
}

export type MenuChild = {
  name: string;
  slug: string;
  type: number;
  children?: MenuChild[];
  categoryId?: number;
};

export type MenuData = {
  id: number;
  name: string;
  data: MenuChild[];
  type: number;
};

// Decouples URL path routing format from raw integer types
const getHref = (type: number, slug: string) => {
  switch (type) {
    case 1:
      return `/pages/${slug}`;
    case 2:
      return `/collections/${slug}`;
    case 3:
      return `/${slug}`;
    default:
      return `/${slug}`;
  }
};

/**
 * Transforms the raw hierarchical categories API payload into standard MenuNode trees,
 * and builds a flat slug lookup index to prevent recursive operations.
 */
export function adaptMenuData(rawMenuData: MenuData): {
  nodes: MenuNode[];
  lookupMap: Map<string, MenuNode>;
} {
  const lookupMap = new Map<string, MenuNode>();

  const transformNode = (raw: MenuChild): MenuNode => {
    const id = raw.slug || String(raw.categoryId || Math.random());
    
    const node: MenuNode = {
      id,
      label: raw.name,
      href: getHref(raw.type, raw.slug),
      type: raw.type,
      children: raw.children && raw.children.length > 0
        ? raw.children.map(transformNode)
        : undefined,
    };

    lookupMap.set(id, node);
    return node;
  };

  // Convert all top-level categories
  const nodes = rawMenuData.data.map(transformNode);

  return { nodes, lookupMap };
}
